import React, { createContext, useContext, useState, useEffect } from "react";
import { fetchProviderData } from "../data/fetchData";
import { set } from "react-hook-form";

const TabContext = createContext();

export const TabProvider = ({ children, provID, userRole, provRole }) => {
  // Main State
  const [providerInfo, setProviderInfo] = useState({});
  const [premiumInfo, setPremiumInfo] = useState({});
  const [serviceLookup, setServiceLookup] = useState({});
  const [serviceList, setServiceList] = useState([]);
  const [userList, setUserList] = useState([]);
  const [allServices, setAllServices] = useState([]);
  const [allowedServices, setAllowedServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [taxonomies, setTaxonomies] = useState({});
  const [googleAPI, setGoogleAPI] = useState("");
  const [isPremium, setIsPremium] = useState(false);
  const [isReferral, setIsReferral] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [warningMessage, setWarningMessage] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);

  // Tab States
  const [availabilityState, setAvailabilityState] = useState({});
  const [listingDetailsState, setListingDetailsState] = useState({});
  const [usersState, setUsersState] = useState({});
  const [premiumState, setPremiumState] = useState({});
  const [settingsState, setSettingsState] = useState({});

  // Filtered States
  const [filteredAdvSearch, setFilteredAdvSearch] = useState([]);
  const [filteredAmenities, setFilteredAmenities] = useState([]);
  const [filteredCommonAreas, setFilteredCommonAreas] = useState([]);
  const [filteredReimbursement, setFilteredReimbursement] = useState([]);
  const [filteredRoomCare, setFilteredRoomCare] = useState([]);
  const [filteredShowAdvSearch, setFilteredShowAdvSearch] = useState([]);
  const [filteredShowAmenities, setFilteredShowAmenities] = useState([]);
  const [filteredShowCommonAreas, setFilteredShowCommonAreas] = useState([]);

  // UI States
  const [changesMade, setChangesMade] = useState(false);
  const [premiumChangesMade, setPremiumChangesMade] = useState(false);
  const [checkChange, setCheckChange] = useState(false);
  const [saving, setSaving] = useState(false);
  const [servicePending, setServicePending] = useState(false);
  const [serviceChange, setServiceChange] = useState(false);
  const [deletedImageIds, setDeletedImageIds] = useState([]);

  // Availability States
  const [availabilityList, setAvailabilityList] = useState([]);
  const [advServices, setAdvServices] = useState([]);
  const [inquiryEmail, setInquiryEmail] = useState("");
  const [notAccepting, setNotAccepting] = useState(false);
  const [notAcceptReason, setNotAcceptReason] = useState("");
  const [providerID, setProviderID] = useState(provID);

  // Premium States
  const [provDescription, setProvDescription] = useState("");
  const [companyLink, setCompanyLink] = useState("");
  const [facebookLink, setFacebookLink] = useState("");
  const [twitterLink, setTwitterLink] = useState("");
  const [instagramLink, setInstagramLink] = useState("");
  const [tourLink, setTourLink] = useState("");
  const [videoLink, setVideoLink] = useState("");
  const [amenitiesList, setAmenitiesList] = useState([]);
  const [amenitiesChecked, setAmenitiesChecked] = useState([]);
  const [commonAreasList, setCommonAreasList] = useState([]);
  const [commonAreasChecked, setCommonAreasChecked] = useState([]);
  const [companyLogoURL, setCompanyLogoURL] = useState("");
  const [brochureURL, setBrochureURL] = useState("");
  const [floorPlanURL, setFloorPlanURL] = useState("");
  // State: current gallery images
  const [galleryImages, setGalleryImages] = useState([]);

  // Error State

  const [uploadError, setUploadError] = useState("");

  const fetchAndSetData = async provID => {
    try {
      const data = await fetchProviderData(provID);

      setTaxonomies(data.taxonomies);
      setProviderInfo(data.provider_info);
      setPremiumInfo(data.premium_info);
      setServiceLookup(data.service_lookup);
      setServiceList(data.services);
      setAvailabilityState(data.availability);
      setUserList(data.user_list);
      setAvailabilityList(data.availability);
      setGoogleAPI(data.google_key);
      setServicePending(data.awaiting_approval);

      const filteredServices = data.taxonomies.services.filter(service => service.display_service === "1");
      setIsReferral(data.provider_info.is_referral == 1 || data.provider_info.is_referral === "1");

      console.log("services listsss", data.services);
      setAllServices(filteredServices);

      const termIds = new Set(data.services.map(service => parseInt(service.term_id, 10)));
      const filteredAllowedServices = filteredServices.filter(service => termIds.has(service.term_id));

      setAllowedServices(filteredAllowedServices);
      setLoading(false);
    } catch (error) {
      console.error("Error setting provider info:", error);
    }
  };

  // Filter taxonomies effect
  useEffect(() => {
    // console.log("use effect");
    // console.log("taxonomies", taxonomies);
    // console.log("service list", serviceList);
    // console.log("service lookup", serviceLookup);
    if (Object.keys(taxonomies).length > 0 && serviceList.length > 0) {
      const combinedTax = [...taxonomies.advanced_services, ...taxonomies.amenities, ...taxonomies.common_areas, ...taxonomies.payment_types, ...taxonomies.room_types];

      const termNameMap = new Map(combinedTax.map(term => [term.term_id, term.name]));

      const allowedTermIDs = new Set(allowedServices.map(service => parseInt(service.term_id)));

      // console.log("allowed term ids", allowedTermIDs);

      const uniqueTaxID = new Set();
      const filteredServiceLookup = serviceLookup
        .filter(service => allowedTermIDs.has(parseInt(service.service_id, 10)))
        .map(service => ({
          ...service,
          taxonomy_name: termNameMap.get(parseInt(service.taxonomy_id, 10))
        }))
        .filter(service => {
          if (uniqueTaxID.has(service.taxonomy_id)) return false;
          uniqueTaxID.add(service.taxonomy_id);
          return true;
        });

      const filteredDisplayAmenities = serviceList.filter(service => {
        allowedTermIDs.has(parseInt(service.service_id, 10));
      });

      //console.log("FILTERED DISPLAY AMENITIES", filteredDisplayAmenities);

      const allServices = serviceLookup
        .filter(item => allowedTermIDs.has(parseInt(item.service_id, 10)))
        .map(item => ({
          ...item,
          taxonomy_name: termNameMap.get(parseInt(item.taxonomy_id, 10))
        }));

      //console.log("FILTERED SERVICE LOOKUP", filteredServiceLookup);

      //console.log("SERVICE LIST", serviceList);
      // For advanced_search
      setFilteredAdvSearch(filteredServiceLookup.filter(service => service.type === "advanced_search" && service.taxonomy_name !== undefined));

      // For room_care
      setFilteredRoomCare(allServices.filter(service => service.type === "room_care" && service.taxonomy_name !== undefined));

      // For reimbursement
      setFilteredReimbursement(allServices.filter(service => service.type === "reimbursement" && service.taxonomy_name !== undefined));

      // For amenities
      setFilteredAmenities(filteredServiceLookup.filter(item => item.type === "amenity" && item.taxonomy_name !== undefined));

      // For common areas
      setFilteredCommonAreas(filteredServiceLookup.filter(item => item.type === "common_area" && item.taxonomy_name !== undefined));

      const advServOptions = taxonomies.display_options.advanced_services;
      const amenityOptions = taxonomies.display_options.amenities;
      const commonAreaOptions = taxonomies.display_options.common_areas;

      const shouldShowAdvSearch = advServOptions.some(item => {
        return allowedTermIDs.has(item.term_id) && item.advanced_search_options === "1";
      });

      const shouldShowAmenities = amenityOptions.some(item => {
        return allowedTermIDs.has(item.term_id) && item.amenities_options === "1";
      });

      const shouldShowCommonAreas = commonAreaOptions.some(item => {
        return allowedTermIDs.has(item.term_id) && item.common_areas_options === "1";
      });

      setFilteredShowAdvSearch(shouldShowAdvSearch);
      setFilteredShowAmenities(shouldShowAmenities);
      setFilteredShowCommonAreas(shouldShowCommonAreas);
    }
  }, [taxonomies, serviceList, serviceLookup, allowedServices]);

  console.log("SERVICE LIST", filteredAdvSearch);
  // Provider info effect
  useEffect(() => {
    if (providerInfo.adv_services) {
      const setAdvancedSearch = providerInfo.adv_services.split(",").map(id => parseInt(id.trim()));
      setAdvServices(setAdvancedSearch);
    }

    if (premiumInfo?.email) {
      setInquiryEmail(premiumInfo.email);
    } else {
      setInquiryEmail("");
    }

    //console.log("provider info", providerInfo);

    if (providerInfo.accepting) {
      setNotAccepting(providerInfo.accepting === "1" ? true : false);
      setNotAcceptReason(providerInfo.accepting === "1" ? providerInfo.accepting_reason : "");
    } else {
      setNotAccepting(false);
    }

    //console.log("accepting", providerInfo.accepting);
    //console.log("reason", providerInfo.accepting_reason);

    if (providerInfo) {
      setIsPremium(providerInfo.profile === "1");
    }
  }, [providerInfo, premiumInfo]);

  // Premium info effect
  useEffect(() => {
    if (premiumInfo) {
      //console.log("DESC", premiumInfo.description);
      setProvDescription(premiumInfo.description || "");
      setCompanyLink(premiumInfo.url || "");
      setFacebookLink(premiumInfo.comp_facebook || "");
      setTwitterLink(premiumInfo.comp_twitter || "");
      setInstagramLink(premiumInfo.comp_ig || "");
      setTourLink(premiumInfo.tour || "");
      setVideoLink(premiumInfo.youtube || "");

      const amenitiesList = filteredAmenities.map(amenity => ({
        id: amenity.taxonomy_id,
        name: amenity.taxonomy_name
      }));
      const commonAreasList = filteredCommonAreas.map(commonArea => ({
        id: commonArea.taxonomy_id,
        name: commonArea.taxonomy_name
      }));
      setAmenitiesList(amenitiesList);
      setCommonAreasList(commonAreasList);

      if (premiumInfo.amenities_new) {
        setAmenitiesChecked(premiumInfo.amenities_new.split(",").map(id => parseInt(id.trim())));
      } else {
        setAmenitiesChecked([]);
      }

      if (premiumInfo.common_areas_new) {
        setCommonAreasChecked(premiumInfo.common_areas_new.split(",").map(id => parseInt(id.trim())));
      } else {
        setCommonAreasChecked([]);
      }
    } else {
      // Reset premium values
      setProvDescription("");
      setCompanyLink("");
      setFacebookLink("");
      setTwitterLink("");
      setInstagramLink("");
      setTourLink("");
      setVideoLink("");
      setAmenitiesChecked([]);
      setCommonAreasChecked([]);
    }
  }, [premiumInfo, filteredAmenities, filteredCommonAreas]);

  // Media URLs effect
  useEffect(() => {
    if (premiumInfo) {
      setCompanyLogoURL(premiumInfo.logo || "");
      setBrochureURL(premiumInfo.brochure || "");
      setFloorPlanURL(premiumInfo.floorplan || "");
    }
  }, [premiumInfo]);

  const updateAvailabilityList = (index, newItem) => {
    setAvailabilityList(prevList => {
      const newList = [...prevList];
      newList[index] = newItem;
      return newList;
    });
  };

  // Do a check in user role for admin to set this as true
  useEffect(() => {
    if (userRole === "admin") {
      setIsAdmin(true);
    }
  }, [userRole]);

  return (
    <TabContext.Provider
      value={{
        availabilityState,
        setAvailabilityState,
        listingDetailsState,
        setListingDetailsState,
        usersState,
        setUsersState,
        premiumState,
        setPremiumState,
        settingsState,
        setSettingsState,
        providerInfo,
        premiumInfo,
        serviceLookup,
        serviceList,
        userList,
        allServices,
        allowedServices,
        loading,
        fetchAndSetData,
        provID,
        userRole,
        taxonomies,
        filteredAdvSearch,
        filteredAmenities,
        filteredCommonAreas,
        filteredReimbursement,
        filteredRoomCare,
        advServices,
        setAdvServices,
        notAccepting,
        setNotAccepting,
        notAcceptReason,
        setNotAcceptReason,
        providerID,
        setProviderID,
        inquiryEmail,
        setInquiryEmail,
        provDescription,
        setProvDescription,
        companyLink,
        setCompanyLink,
        facebookLink,
        setFacebookLink,
        twitterLink,
        setTwitterLink,
        instagramLink,
        setInstagramLink,
        tourLink,
        setTourLink,
        videoLink,
        setVideoLink,
        amenitiesList,
        setAmenitiesList,
        amenitiesChecked,
        setAmenitiesChecked,
        commonAreasList,
        setCommonAreasList,
        commonAreasChecked,
        setCommonAreasChecked,
        companyLogoURL,
        setCompanyLogoURL,
        availabilityList,
        setAvailabilityList,
        updateAvailabilityList,
        changesMade,
        setChangesMade,
        brochureURL,
        setBrochureURL,
        floorPlanURL,
        setFloorPlanURL,
        checkChange,
        setCheckChange,
        saving,
        setSaving,
        setUserList,
        deletedImageIds,
        setDeletedImageIds,
        provRole,
        googleAPI,
        serviceChange,
        setServiceChange,
        servicePending,
        setServicePending,
        isPremium,
        isReferral,
        errorMessage,
        setErrorMessage,
        warningMessage,
        setWarningMessage,
        galleryImages,
        setGalleryImages,
        isAdmin,
        filteredShowAdvSearch,
        filteredShowAmenities,
        filteredShowCommonAreas,
        uploadError,
        setUploadError,
        premiumChangesMade,
        setPremiumChangesMade
      }}
    >
      {children}
    </TabContext.Provider>
  );
};

export const useTabContext = () => useContext(TabContext);
